import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CreateNewWorkspaceInfo, NewWorkspaceInfo } from '../../models/workspace.model';
import { AuthService } from '../../../shared/services/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UploadModalComponent } from '../../../on-boarding/profile-avatar/upload-modal/upload-modal.component';
import { UploadService } from '../../../shared/services/upload.service';
import { ApiService } from '../../../shared/services/api.service';
import { Router } from '@angular/router';
import { WorkspaceService } from '../../../shared/services/workspace.service';
import { EmitterService } from '../../../shared/services/emitter.service';
import { User, WorkspaceAvatarUpdatedEvent, WorkspaceDTO, WorkspaceUpdatedEvent } from '../../../shared/interfaces';
import { OverlayService } from '../../../shared/services/overlayService';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { MtmBreadcrumbService } from '../../../shared/services/mtm-breadcrumb.service';
import { ThemeService } from '../../../shared/services/theme.service';
import {
  WORKSPACE_LABELS,
  WORKSPACE_ERRORS,
  WORKSPACE_SUCCESS,
  WORKSPACE_MODAL_CONFIG,
  WORKSPACE_PATHS,
  WORKSPACE_KEYS,
  STATES,
} from '../constants/workspace.constants';

@Component({
  selector: 'mtm-create-workspace',
  templateUrl: './create-workspace.component.html',
  styleUrl: './create-workspace.component.scss',
  standalone: false,
})
export class CreateWorkspaceComponent implements OnInit, OnDestroy {
  @Input() companyId: string;
  @Input() brand: any;
  @Input() asModal: boolean = false;

  workspaceForm: UntypedFormGroup;
  model: NewWorkspaceInfo = CreateNewWorkspaceInfo();
  isSaving: boolean = false;
  fromBandW: boolean = false;
  brandId: string;

  private readonly ngUnsubscribe = new Subject<void>();
  private modal: NgbModalRef;
  private authUser: User | null = null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly workspaceService: WorkspaceService,
    private readonly uploadService: UploadService,
    private readonly apiService: ApiService,
    private readonly modalService: NgbModal,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly themeService: ThemeService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe,
    private readonly activeModal: NgbActiveModal,
    private readonly analyticService: AnalyticsService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.setupForm();
    this.applyThemeAndBreadcrumbDefaults();
    this.initBrandSubscription();
    this.brandId = this.brand?.id || localStorage.getItem(WORKSPACE_KEYS.BRAND_ID);
    this.setBreadcrumb();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.mtmBreadcrumbService.clearLinks();
  }

  cancel(): void {
    if (this.asModal) {
      this.activeModal.close();
    } else {
      this.router.navigate([WORKSPACE_PATHS.WORKSPACES]).then(() => {});
    }
  }

  trimString(value: string): string {
    return value ? value.trim() : '';
  }

  submit(): void {
    const nameControl = this.workspaceForm.get(WORKSPACE_KEYS.NAME);
    if (nameControl) {
      nameControl.setValue(this.trimString(nameControl.value));
    }

    this.validateFormFields(this.workspaceForm);

    if (!this.workspaceForm.valid) {
      return;
    }

    this.saveWorkspace();
  }

  fileChangeListener($event: any): void {
    const files = $event?.files?.[0];
    if (!files) return;

    this.openModal();
    this.modal.componentInstance.imageFile = files;

    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      this.setImageOnModal();
    };
    fileReader.readAsDataURL(files);
  }

  removeLeadingSpaces(): void {
    const nameControl = this.workspaceForm.get(WORKSPACE_KEYS.NAME);
    if (!nameControl) return;

    const value = typeof nameControl.value === WORKSPACE_KEYS.STRING ? nameControl.value : '';
    const cleaned = value.trimStart().replace(/[^a-zA-Z0-9 ]/g, '');
    nameControl.setValue(cleaned, { emitEvent: false });
  }

  private validateFormFields(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateFormFields(control);
      }
    });
  }

  saveWorkspace(): void {
    const { authUser } = this;
    // Membership is not part of the workspace payload anymore (RBAC migration);
    // users are added after creation via the workspace permissions screen.
    const workspaceData: WorkspaceDTO = {
      companyId: this.companyId || authUser.companyId,
      name: this.workspaceForm.value.name,
      mode: 'drive',
    };

    this.isSaving = true;

    this.workspaceService
      .createWorkspace(workspaceData, this.brandId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: any) => this.handleCreateWorkspaceResult(result, workspaceData),
        error: (error) => this.handleCreateWorkspaceError(error, workspaceData),
      });
  }

  private handleCreateWorkspaceResult(result: any, workspaceData: any): void {
    this.analyticService.clearAnalyticsCache();
    if (this.model.imageUrl) {
      this.uploadAvatarIfNeeded(result, workspaceData);
    } else {
      this.router.navigate([WORKSPACE_PATHS.WORKSPACE_EDIT, result.id]).then(() => {});
    }

    this.overlayService.showSuccess(WORKSPACE_SUCCESS.CREATED, 'Success');
  }

  private uploadAvatarIfNeeded(result: any, workspaceData: any): void {
    const { authUser } = this;
    const workspaceUrl =
      this.apiService.baseUrl + `/api/workspace/${this.companyId || authUser.company.id}/${result.id}/avatar`;

    this.uploadService.uploadImage(this.model.imageUrl, workspaceUrl).subscribe({
      next: (uploadResult: any) => {
        EmitterService.get(WorkspaceUpdatedEvent).emit(workspaceData);

        EmitterService.get(WorkspaceAvatarUpdatedEvent).emit({
          workspaceId: result.id,
          avatarUrl: uploadResult.avatarUrl,
        });

        if (this.brand && !this.fromBandW) {
          this.activeModal.close(workspaceData);
        }

        this.router.navigate([WORKSPACE_PATHS.WORKSPACE_EDIT, result.id]).then(() => {});
      },
      error: (uploadError: any) => {
        this.overlayService.showError(WORKSPACE_ERRORS.AVATAR_UPLOAD_FAILED, 'Error');
        console.error(uploadError);
      },
    });
  }

  private handleCreateWorkspaceError(error: any, workspaceData: any): void {
    this.isSaving = false;
    const errorCode = error?.errorCode;
    const errorMessageKey =
      errorCode === STATES.DUPLICATE_NAME ? WORKSPACE_ERRORS.DUPLICATE_NAME : WORKSPACE_ERRORS.GENERIC;

    this.overlayService.showError(this.translatePipe.transform(errorMessageKey, { name: workspaceData.name }), 'Error');
  }

  private setupForm(): void {
    this.workspaceForm = this.fb.group({
      name: ['', Validators.required],
    });
  }

  private initBrandSubscription(): void {
    this.analyticService.selectedBrand.pipe(takeUntil(this.ngUnsubscribe)).subscribe((brand) => {
      if (brand) {
        this.brand = brand;
        this.fromBandW = true;
      }
    });
  }

  private applyThemeAndBreadcrumbDefaults(): void {
    this.mtmBreadcrumbService.clearLinks();
    this.themeService.updateContent({
      fullWidthContent: true,
    });
  }

  private setBreadcrumb(): void {
    const links: any[] = [];
    const brand = this.authService.getMyBrand();

    if (brand) {
      links.push({ label: brand.name });
    }

    links.push({ label: WORKSPACE_LABELS.WORKSPACES, url: [WORKSPACE_PATHS.WORKSPACES] });
    links.push({ label: WORKSPACE_LABELS.NEW_WORKSPACES });

    this.mtmBreadcrumbService.setLinks(links);
  }

  private openModal(): void {
    this.modal = this.modalService.open(UploadModalComponent, {
      size: WORKSPACE_MODAL_CONFIG.SIZE,
      windowClass: WORKSPACE_MODAL_CONFIG.WINDOW_CLASS,
    });
    this.modal.componentInstance.resizeToWidth = WORKSPACE_MODAL_CONFIG.AVATAR_WIDTH;
    this.modal.componentInstance.aspectRatio = WORKSPACE_MODAL_CONFIG.AVATAR_ASPECT_RATIO;
    this.modal.componentInstance.maintainAspectRatio = true;
  }

  private setImageOnModal(): void {
    this.modal.result.then(
      (result: string) => {
        if (!result) return;
        this.model.imageUrl = result;
        this.cdr.detectChanges();
      },
      () => {}
    );
  }
}
